<template>
    <div
        id="app"
        :class="{
            'is-mobile': isMobile,
            'is-index': !isLoggedIn || isIndexPageV1,
            'is-style-v2': useV2,
        }"
    >
        <veui-loading
            :loading="loading"
            :class="{
                'cc-index-loading': true,
                'is-index': !isLoggedIn || isIndexPageV1,
            }"
            ui="strong l"
        />
        <router-view/>
        <message-center-dialog v-if="showMessageCenterDialog"/>
        <send-trial-coupon-dialog v-if="isNeedTrialCoupon"/>
    </div>
</template>

<script>
/* globals recordSdk */

import {isEmpty, pick} from 'lodash';
import {Loading} from 'veui';
import {getModel} from './lib/model';
import HairuoRequest from './lib/request/Hairuo';
import {sendAlsLog, sendN8nLog} from './lib/als.js';
import {getRouteQuery, getTokenFromCookie, getUseridFromCokkie, urlQueryDecode} from '@/common/util';
import {eventNameMap} from './config/customEventName';
import BasicInfoModel from './model/BasicInfo';
import FeedbackModel from './model/Feedback';
import MessageModel from './model/Message';
import CouponModel from './model/Coupon';
import {createRecord} from './model/WebRecord';
import TextCreateModel from './model/TextCreate';
import alert from 'veui/plugins/alert';
import confirm from 'veui/plugins/confirm';
import prompt from 'veui/plugins/prompt';
import toast from 'veui/plugins/toast';
import {BaiduTongjiLogger} from '@baidu/cc-creative-sdk';
import {APP_ID_BASS, tongjiToken} from '@/common/config';
import guid from './lib/guid';
const loggers = [new BaiduTongjiLogger(tongjiToken)];
import Questionnaire from '@baidu/agile-survey';
import MessageCenterDialog from '@/component/MessageDialog/messageCenterDialog';
import SendTrialCouponDialog from '@/component/MessageDialog/SendTrialCouponDialog';
const MaxFontSize = 25;

import 'veui-theme-dls-icons/video-solid';
import 'veui-theme-dls-icons/image-solid';
import 'veui-theme-dls-icons/text-square-solid';
import 'veui-theme-dls-icons/smiley-star-solid';
import 'veui-theme-dls-icons/arrow-left';
import 'veui-theme-dls-icons/image-star-solid';
import 'veui-theme-dls-icons/video-film-solid';
import 'veui-theme-dls-icons/folder-download-solid';

export default {
    data() {
        return {
            useV2: false,
            isMobile: false,
            basicConfig: {},
            recordIns: {},
            recordTimer: 0,
            envType: '',
            recordKey: ''
        };
    },
    components: {
        'veui-loading': Loading,
        'message-center-dialog': MessageCenterDialog,
        'send-trial-coupon-dialog': SendTrialCouponDialog
    },
    watch: {
        $route(to, from) {
            // 路由切换，从子页面跳到首页时，检查是否要显示移动版
            if (from.name != null && from.name.indexOf('home') < 0) {
                this.isMobile = false;
                this.checkIsMobile();
            }
            window.scrollTo({top: 0});

            this.useV2 = this.getUseV2();

            this.$nextTick(() => {
                this.checkEnv();

                if (this.isWhiteLogUser) {
                    // 白名单用户，页面路由变化，发送日志，记录当前页面
                    let params = {
                        type: 'route_change',
                        fromRoute: from.name
                    };
                    this.n8nLog(params);
                }
            });

            this.recordKey = window.location.pathname;
        },
        shouldStop(to, from) {
            if (typeof recordSdk === 'undefined') {
                return;
            }
            // 从stop=false 变成 stop=true，需停止并上报；
            if (to && !from) {
                console.log('stop');
                this.destroyRecord();
            }
            // 从stop=true 变成 stop=false，需开始录制
            if (!to && from) {
                console.log('start');
                this.beginRecord();
            }
        },
        isMobile(to, from) {
            // 移动版变化，重算基础fontsize
            this.calcFontSize();
        }
    },
    computed: {
        shouldStop() {
            let match = this.$route?.matched || [];
            let noRecord = match.filter(item => {
                return item.meta?.record === false;
            });
            let stop = noRecord.length > 0 || match.length < 1 || match[0].path === '/exception';
            return stop;
        },
        loadingInfo() {
            return this.$store.state.app.loadingInfo || false;
        },
        // 是否是登录页
        isLoginPage() {
            let routeName = this.$route.name || '';
            return routeName.toLowerCase().indexOf('login') >= 0;
        },
        // 是否是旧版登录后的首页
        isIndexPageV1() {
            let routeName = this.$route.name || '';
            return routeName.toLowerCase() === 'home';
        },
        isCoExtensionPages() {
            const coExtensionPages = ['coExtensionHome', 'coExtensionMarket', 'coExtensionMarketDetail'];
            return coExtensionPages.includes(this.$route.name);
        },
        isDoc() {
            let routeName = this.$route.name || '';
            const docPages = ['aboutUsNoAuth'];
            const res = routeName === 'helpDoc' || docPages.includes(routeName);
            return res;
        },
        userName() {
            return this.$store.state.app.userInfo?.accountInfo?.userName || '';
        },
        userId() {
            return this.$store.state.app.hairuo?.userid || -1;
        },
        optId() {
            return this.$store.state.app.hairuo?.optid || -1;
        },
        // 是否有新版本权限
        isStyleV2() {
            return this.$store.state.app.userInfo.isStyleV2;
        },
        isRealStyleV2() {
            return this.$store.state.app.userInfo.isRealStyleV2;
        },
        // 是否已登录
        isLoggedIn() {
            return this.userName;
        },
        needAuth() {
            return this.$route.meta?.authorizedRole || false;
        },
        isOuterUser() {
            return this.$store.state.app.userInfo.isOuterUser;
        },
        hags() {
            return this.$store.state.app.userInfo.bizHags;
        },
        hagOnlyDigital() {
            return this.hags.digital_is_passport;
        },
        // 是否发附加日志
        isWhiteLogUser() {
            return this.hags.qingduo_image_ip_thumbnail_generate === true;
        },
        // 点券信息，默认返回空对象避免后续取值报错
        couponInfo() {
            return this.$store.state.app.couponInfo || {};
        },
        // 是否领取过试用包
        getCoupons() {
            return this.couponInfo.getCoupons || false;
        },
        // 外部账号且未领取过试用包，需要获取试用包并展示到账弹窗
        // 登录后才展示推送消息 && 共拓相关页面不需要展

        isNeedTrialCoupon() {
            return this.userName && !this.isCoExtensionPages && this.isOuterUser && !this.getCoupons;
        },
        contextualPopupApis() {
            let mock = {prototype: {}};
            const plugins = [alert, confirm, prompt, toast];
            plugins.forEach(plugin => plugin.install(mock));
            return mock.prototype;
        },
        hairuoRequest() {
            if (isEmpty(this.$store.state.app.hairuo)) {
                return null;
            }
            return new HairuoRequest({
                ...this.$store.state.app.hairuo,
                n8nLog: this.n8nLog.bind(this),
                onredirect: () => {
                    // this.showLoginTip = true;
                },
                onerror: err => {
                    let data = pick(err, ['name', 'message', 'stack']);
                    if (err.config) {
                        let {url, baseURL, method, data: body} = err.config;
                        if (body.length > 256) {
                            body = body.substring(0, 256) + '...';
                        }
                        data.request = {url: baseURL + url, method, body};
                    }
                }
            });
        },
        hairuoSpecialRequest() {
            if (isEmpty(this.$store.state.app.hairuo)) {
                return null;
            }
            const params = {apiPath: '/hairuo_console/request.ajax'};
            return new HairuoRequest({
                ...this.$store.state.app.hairuo,
                ...params,
                onredirect: () => {
                },
                onerror: err => {
                    let data = pick(err, ['name', 'message', 'stack']);
                    if (err.config) {
                        let {url, baseURL, method, data: body} = err.config;
                        if (body.length > 256) {
                            body = body.substring(0, 256) + '...';
                        }
                        data.request = {url: baseURL + url, method, body};
                    }
                }
            });
        },
        textCreateModel() {
            return new TextCreateModel({
                request: this.hairuoRequest
            });
        },
        imageCreateModel() {
            return new (getModel('ImageCreate'))({
                request: this.hairuoRequest
            });
        },
        videoCreateModel() {
            return new (getModel('VideoCreate'))({
                request: this.hairuoRequest
            });
        },
        viralFissionModel() {
            return new (getModel('ViralFission'))({
                request: this.hairuoRequest
            });
        },
        ApplyRoleModel() {
            return new (getModel('ApplyRole'))({
                request: this.hairuoRequest
            });
        },
        CouponModel() {
            return new CouponModel({
                request: this.hairuoRequest
            });
        },
        CoExtensionModel() {
            return new (getModel('CoExtension'))({
                request: this.hairuoRequest
            });
        },
        myCreationModel() {
            return new (getModel('myCreation'))({
                request: this.hairuoRequest
            });
        },
        specialCreateModel() {
            return new (getModel('specialCreate'))({
                request: this.hairuoSpecialRequest
            });
        },
        AiChatCreateModel() {
            return new (getModel('AiChatCreate'))({
                request: this.hairuoRequest
            });
        },
        feedbackModel() {
            return new FeedbackModel({
                request: this.hairuoRequest
            });
        },
        entryOptions() {
            return {
                mode: 'embed',
                config: {
                    hairuo: this.$store.state.app.hairuo
                },
                loggers
            };
        },
        OrderModel() {
            return new (getModel('Order'))({
                request: this.hairuoRequest
            });
        },
        MessageModel() {
            return new MessageModel({
                request: this.hairuoRequest
            });
        },
        DiagnoseModel() {
            return new (getModel('DiagnoseModel'))({
                request: this.hairuoRequest
            });
        },
        image2VideoModel() {
            return new (getModel('Image2Video'))({
                request: this.hairuoRequest
            });
        },
        videoCompositionModel() {
            return new (getModel('videoComposition'))({
                request: this.hairuoRequest
            });
        },
        digitalCommonModel() {
            return new (getModel('digitalCommonModel'))({
                request: this.hairuoRequest
            });
        },
        basicInfoModel() {
            return new BasicInfoModel({
                request: this.hairuoRequest
            });
        },
        loading() {
            const {loadingInfo, userName} = this;
            const needLogin = this.$route.meta.allowNotLogin !== true;
            const status = needLogin && (loadingInfo || !userName);
            return status;
        },
        showMessageCenterDialog() {
            // 登录后才展示推送消息 && 共拓相关页面不需要展
            return this.userName && !this.isCoExtensionPages && !this.hagOnlyDigital;
        }
    },
    provide() {
        let model = {};
        // 用到时再去 new Model
        Object.defineProperties(model, {
            textCreate: {get: () => this.textCreateModel},
            imageCreate: {get: () => this.imageCreateModel},
            videoCreate: {get: () => this.videoCreateModel},
            viralFission: {get: () => this.viralFissionModel},
            applyRole: {get: () => this.ApplyRoleModel},
            coupon: {get: () => this.CouponModel},
            coExtension: {get: () => this.CoExtensionModel},
            specialCreate: {get: () => this.specialCreateModel},
            order: {get: () => this.OrderModel},
            AiChatCreate: {get: () => this.AiChatCreateModel},
            myCreation: {get: () => this.myCreationModel},
            feedback: {get: () => this.feedbackModel},
            message: {get: () => this.MessageModel},
            image2Video: {get: () => this.image2VideoModel},
            basicInfo: {get: () => this.basicInfoModel},
            videoComposition: {get: () => this.videoCompositionModel},
            diagnose: {get: () => this.DiagnoseModel},
            digitalCommon: {get: () => this.digitalCommonModel}
        });
        return {
            ...this.contextualPopupApis,
            model,
            entryOptions: this.entryOptions,
            sendCustomEvent: this.sendCustomEvent,
            alsLog: this.alsLog
        };
    },
    created() {
    },
    mounted() {
        // 线上强制转https
        if (window.location.protocol === 'http:' && process.env.VUE_APP_VERSION === 'online') {
            window.location.protocol = 'https:';
        }

        if (window.location.href.indexOf('_and_') !== -1) {
            window.location.href = urlQueryDecode(window.location.href);
        }

        this.checkIsMobile();
        this.calcFontSize();
        window.addEventListener('resize', () => {
            return (() => {
                this.checkIsMobile();
                this.calcFontSize();
            })();
        });
        this.getQuestionnaire();

        // 标记环境类型
        this.checkEnv();

        this.useV2 = this.getUseV2();

        this.recordKey = window.location.pathname;

        // 为了录屏区分账户操作场景，通过session记录唯一ID，打开页面时初始化，新开页面的不共享；当前页面刷新可保留
        this.checkWebRecordSessionId();

        this.$nextTick(() => {
            if (this.shouldStop !== true) {
                this.beginRecord();
            }

            if (this.isWhiteLogUser) {
                // 白名单用户，初始进入页面，发送日志，记录当前页面
                let params = {
                    type: 'page_enter'
                };
                console.log('params', params);
                this.n8nLog(params);
            }
        });
    },
    methods: {
        // 计算是否使用v2版本
        getUseV2() {
            return (this.isStyleV2 && window.sessionStorage.style_click_old !== '1')
                || (this.isRealStyleV2 && window.sessionStorage.style_click_old !== '1');
        },
        checkWebRecordSessionId() {
            // 为了录屏区分账户操作场景，通过session记录唯一ID，打开页面时初始化，手动新开页面的不共享；当前页面刷新可保留
            let webrecordSessionId = window.sessionStorage.webrecord_session_id || '';
            if (!webrecordSessionId) {
                webrecordSessionId = 'rrweb_' + guid();
                window.sessionStorage.webrecord_session_id = webrecordSessionId;
            }
        },
        // 根据环境变量决定是否增加右上角的提示
        checkEnv() {
            let envType = process.env.VUE_APP_VERSION;
            this.envType = envType;
            if (envType === 'preonline'
                || envType === 'offline'
                || envType === 'dev'
            ) {
                // 详情页和首页的位置不同
                const div = document.createElement('div');
                div.style.cssText = `
                    margin-right: 20px;
                    font-size: 12px;
                    color: red;
                    cursor: pointer;
                `;
                const tipId = 'not-online-environment-tip';
                let tipText = '';
                switch (envType) {
                    case 'dev':
                        tipText = '本地开发环境';
                        break;
                    case 'offline':
                        tipText = '线下测试环境';
                        break;
                    case 'preonline':
                        tipText = '预上线环境';
                        break;
                    default:
                        tipText = '';
                }
                div.innerText = tipText;
                div.title = '双击关闭此提示';

                div.ondblclick = () => {
                    div.parentElement.removeChild(div);
                };
                if (!tipText && document.getElementById(tipId)) {
                    document.getElementById(tipId).remove();
                    return false;
                }

                let headerRight = document.getElementsByClassName('cc-header-right')[0]
                    || document.getElementsByClassName('home-nav-right')[0];
                if (tipText && document.getElementById(tipId) == null && headerRight) {
                    div.id = tipId;
                    // 追加到右上角的dom里
                    headerRight.insertBefore(div, headerRight.children[0]);
                }
            }
        },
        alsLog(data) {
            const userid = this.$store.state.app.hairuo?.userid;
            const optid = this.$store.state.app.hairuo?.optid;
            const tradeName = this.$store.state.app.userInfo?.accountInfo?.tradeName;
            const trade2Name = this.$store.state.app.userInfo?.accountInfo?.trade2Name;
            let usrTradeInfo = '';
            if (!tradeName) {
                usrTradeInfo = '无';
            }
            else {
                usrTradeInfo = tradeName + '_' + (trade2Name ? `(${trade2Name})` : '无');
            }
            if (!userid && !this.isDoc && !this.isCoExtensionPages && !this.isLoginPage) {
                return false;
            }
            if (data.f5 == null) {
                data.f5 = this.$route.name;
            }
            return sendAlsLog({
                ...data,
                f3: userid,
                f13: usrTradeInfo,
                f14: optid
            });
        },
        n8nLog(data) {
            if (this.isWhiteLogUser !== true) {
                return;
            }
            const userid = this.$store.state.app.hairuo?.userid;
            const optid = this.$store.state.app.hairuo?.optid;
            const userName = this.$store.state.app.userInfo?.accountInfo?.userName;
            const tradeName = this.$store.state.app.userInfo?.accountInfo?.tradeName;
            const trade2Name = this.$store.state.app.userInfo?.accountInfo?.trade2Name;
            let tradeInfo = '';
            if (!tradeName) {
                tradeInfo = '';
            }
            else {
                tradeInfo = tradeName + '_' + (trade2Name ? `${trade2Name}` : '无');
            }
            let params = {
                ...data,
                userid,
                tradeInfo,
                optid,
                userName: userName || '',
                routeName: this.$route.name,
                routePath: this.$route.path
            };
            return sendN8nLog(params);
        },
        checkIsMobile() {
            let routeName = this.$route.name || '';
            let isMobile = false;
            let mobilePattern = /Mobi|Android|iPhone/i;
            // 当前只有位于首页时，才判断可显示移动版
            if ((routeName.indexOf('home') >= 0 || routeName.indexOf('login') >= 0)
                && (mobilePattern.test(navigator.userAgent)
                    || document.body.clientWidth <= 500
                )
            ) {
                isMobile = true;
            }
            else {
                isMobile = false;
            }
            // 移动版、不是移动页面时，跳到移动版路由
            if (isMobile && this.$route.name?.indexOf('Mobile') < 0) {
                this.$router.push({
                    name: 'homeMobile'
                });
            }
            // 非移动版、路由是移动页面时，跳到非移动版路由
            if (!isMobile && this.$route.name?.indexOf('Mobile') >= 0) {
                this.$router.push({
                    params: this.$route.params,
                    query: getRouteQuery() || {},
                    name: 'login'
                });
            }
            this.isMobile = isMobile;
        },
        calcFontSize() {
            // 自适应的字体
            const deviceWidth = document.body.clientWidth;
            let fontSize = Math.round(deviceWidth / 375 * 100) / 10;
            if (fontSize > MaxFontSize) {
                fontSize = MaxFontSize;
            }
            // 非移动版路由，字号恢复初始
            if ((this.$route.name || '').indexOf('Mobile') < 0) {
                fontSize = 14;
            }
            document.documentElement.style.fontSize = fontSize + 'px';
        },
        getQuestionnaire() {
            const userId = getUseridFromCokkie();
            const token = getTokenFromCookie();
            const options = {
                userid: Number(userId),
                optid: userId,
                appId: APP_ID_BASS,
                token: token
            };
            // eslint-disable-next-line no-unused-vars
            const app = new Questionnaire(options);
        },
        beginRecord() {
            // 仅线上开启
            let envType = process.env.VUE_APP_VERSION;
            if (envType !== 'online' && envType !== 'preonline') {
                return;
            }

            let match = this.$route?.matched || [];
            let noRecord = match.filter(item => {
                return item.meta?.record === false;
            });
            let stop = noRecord.length > 0 || match.length < 1 || match[0].path === '/exception';
            console.log('not record:' + stop);
            if (!stop) {
                const {recordKey} = this;
                // 开启记录
                this.sdkInit(recordKey);
                // 定时上传
                if (this.recordTimer === 0) {
                    this.recordTimer = setInterval(() => {
                        // stop、上报，再参数true表示重新启动监听
                        this.sdkStop(recordKey, true);
                    }, 60000);
                }
                // 测试打点事件功能，作为用法demo，第二个参数可以为空，不为空的话则记录一些必要的value信息，在回放时候分析用
                // try {
                //     recordSdk.setCustomEvent('我的打点', {operation: 'click'});
                // }
                // catch (e) {
                //     console.log(e);
                // }
            }
            else {
                this.destroyRecord();
            }
        },
        // 开启监听，先init，再start
        sdkInit(key) {
            if (this.recordIns[key] == null) {
                try {
                    // 初始化参数、接口方法
                    let initParams = {
                        userName: this.userName,
                        userId: this.userId,
                        userSystem: 'uc'
                    };
                    const api = createRecord;
                    this.recordIns[key] = recordSdk.init(key, api, initParams);
                }
                catch (e) {
                    console.log(e);
                }
            }
            this.sdkStart(key);
        },
        sdkStart(key) {
            if (this.recordIns[key] == null) {
                this.sdkInit(key);
            }
            try {
                recordSdk.start(key);
            }
            catch (e) {
                console.log(e);
            }
        },
        // 销毁监听事件
        destroyRecord() {
            // 仅线上开启
            let envType = process.env.VUE_APP_VERSION;
            if (envType !== 'online' && envType !== 'preonline') {
                return;
            }
            // 取消定时处理并上传
            if (this.recordTimer > 0) {
                clearInterval(this.recordTimer);
            }
            this.sdkStop(this.recordKey);
            return;
        },
        // 停止监听，stop里进行上传，restart指定是否重新开启下一个监听实例
        sdkStop(key, restart = false) {
            if (this.recordIns[key] == null) {
                return;
            }
            try {
                // 防止初始未登录，登录后ID未更新的情况
                let extraParams = {
                    fileUuid: window.sessionStorage.webrecord_session_id || '',
                    userId: this.userId,
                    userName: this.userName
                };
                recordSdk.stop(key, extraParams);
            }
            catch (e) {
                console.log(e);
            }
            if (restart) {
                this.sdkStart(key);
            }
        },
        // 基础的添加自定义打点的方法，封装一下避免多处重复引用的代码
        sendCustomEvent(eventName, eventType = '') {
            // 页面名称，事件中文名
            const title = this.$route.meta.title || this.$route.routeName;
            const typeName = eventNameMap[eventType] || eventType;
            // 非线上环境，不开启打点动作
            if (this.envType !== 'online' && this.envType !== 'preonline') {
                console.log('打点信息', title, eventName, typeName);
                return;
            }
            try {
                recordSdk.setCustomEvent(`【${title}】${eventName}`, typeName);
            }
            catch (e) {
                console.log(e);
            }
        }
    }
};
</script>

<style lang="less">
@import "~veui-theme-dls/common.less";
@import '~veui-theme-dls/typography.less';
@import "@/style/common.less";

@font-face {
    font-family: "Baidu Number Plus";
    src: url('./assets/fonts/BaiduNumberPlus_Medium.ttf');
}
@font-face {
    font-family: "Baidu Number Plus Bold";
    src: url('./assets/fonts/BaiduNumberPlus_Bold.ttf');
}
#app {
    width: 100%;
    height: 100%;
    &:not(.is-index) {
        overflow-x: hidden;
    }
}
// v2新页面设置最小宽度，超出时可横滚
@media screen and (max-width: @screen-min-width) {
    #app.is-style-v2 {
        min-width: @screen-min-width;
        overflow-x: auto;
        &:not(.is-index) {
            overflow-x: auto;
        }
        .veui-header-v2 {
            .top-header {
                position: absolute;
                left: 0;
                right: 0;
                top: 0;
                min-width: @screen-min-width;
            }
        }
    }
}
.uc-common-login {
    #uc-common-login-mask {
        background: transparent;
    }
    #common-login.uc-common-login-layer {
        left: 75%;
        backdrop-filter: blur(125px);
        top: 100px;
        transform: translate(-50%, 0);
    }
    #common-login:not(.uc-common-login-layer) {
        backdrop-filter: blur(155px);
    }
    .change-login {
        #choose-uc-login {
            // display: none;
        }
    }
    & #passport-login .pass-button-submit{
        margin-bottom: 36px !important;
    }
    #check-protocol-container {
        margin-top: -20px;
    }
    & #passport-login.v2-sms.v2-hasRegUrl .pass-form-item-submit .pass-sms-btn,
    & #passport-login.v2-sms.v2-hasRegUrl .pass-form-item-submit .pass-fgtpwd,
    & #passport-login.v2-sms.v2-hasRegUrl .tang-pass-sms .pass-form-item-submit .pass-sms-link-back   {
        top: 375px;//347+12+16
    }
    & #passport-login .tang-pass-footerBar .pass-reglink {
        top: 16px;
    }

    .uc-submit-form,
    .pass-button-submit {
        background: linear-gradient(270.93deg, #3434F4 0.3%, #4C79FF 99.35%) !important;
    }
}

// 全局样式覆盖：SDK图片库和视频库里新增的跳转擎舵链接，为方便修改，直接通过样式设置隐藏
.cc-creative-image-cropper .qingduo-expand-btn,
.cc-creative-image-picker-title-link.qingduo-link-btn,
.cc-creative-video-picker-page .cc-creative-video-picker-qingduo-link.qingduo-link-btn,
.cc-creative-video-picker-page .cc-creative-video-picker-head-buttons.has-qingduo:not(.has-share) {
    display: none!important;
}
// 临时解决低分辨率下 筛选框高度过高出现的展示问题
.cc-digital-type-search-popover {
    .cc-creative-sdk-veui-tooltip {
        max-width: 600px !important;
        .cc-digital-type-group-filter {
            .cc-creative-sdk-veui-radio-button-group {
                margin-bottom: 8px;
            }
        }
    }
}

</style>

<style lang="less" scoped>
.cc-index-loading {
    position: absolute;
    top: 0;
    background: url(https://qiaojiang.bj.bcebos.com/fe/qingduoBannerBg.png);
    background-size: 100% 100%;
    background-repeat: no-repeat;
    background-position: center;
    width: 100%;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: #fff;
    z-index: 200;
    justify-content: center;
    min-height: 600px;
    &.is-index {
        top: 0;
        height: 100vh;
    }
}
</style>
